import React, { useState } from 'react';
import { CheckSquare, Calendar, Mail, FileSearch, ArrowRight } from 'lucide-react';

const Workflows = () => {
  const [activeWorkflow, setActiveWorkflow] = useState(0);
  
  const workflows = [
    {
      title: 'Meeting Assistant',
      icon: <Calendar className="h-6 w-6" />,
      description: 'Let Siva handle your meeting preparations, notes, and follow-ups.',
      steps: [
        'Automatically pulls relevant documents before meetings',
        'Transcribes and summarizes meeting discussions',
        'Creates and assigns action items to team members',
        'Sends follow-up reminders and updates'
      ]
    },
    {
      title: 'Email Management',
      icon: <Mail className="h-6 w-6" />,
      description: 'Siva organizes your inbox and helps you respond to emails efficiently.',
      steps: [
        'Categorizes incoming emails by priority and topic',
        'Drafts responses to common inquiries',
        'Schedules follow-ups for important conversations',
        'Creates task reminders from email content'
      ]
    },
    {
      title: 'Research Assistant',
      icon: <FileSearch className="h-6 w-6" />,
      description: 'Siva gathers information from multiple sources and creates comprehensive reports.',
      steps: [
        'Searches across web, databases, and internal documents',
        'Extracts and organizes relevant information',
        'Generates summaries with key insights',
        'Creates visual data representations when applicable'
      ]
    },
    {
      title: 'Task Automation',
      icon: <CheckSquare className="h-6 w-6" />,
      description: 'Siva helps you automate repetitive tasks to save time and reduce errors.',
      steps: [
        'Records and analyzes your repetitive workflows',
        'Creates automated sequences that run with a single command',
        'Schedules tasks to run at optimal times',
        'Provides reports on time saved and productivity improved'
      ]
    }
  ];

  return (
    <section id="workflows" className="py-20">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Siva in Action</h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            See how Siva can transform your daily workflows with intelligent automation
            and assistance across various scenarios.
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-1 space-y-4">
            {workflows.map((workflow, index) => (
              <button
                key={index}
                className={`w-full text-left p-4 rounded-lg transition-all ${
                  activeWorkflow === index 
                    ? 'bg-gradient-to-r from-slate-800 to-slate-700 border-l-4 border-teal-500 shadow-lg' 
                    : 'hover:bg-slate-800'
                }`}
                onClick={() => setActiveWorkflow(index)}
              >
                <div className="flex items-center gap-3">
                  <div className={`${
                    activeWorkflow === index ? 'text-teal-400' : 'text-slate-400'
                  }`}>
                    {workflow.icon}
                  </div>
                  <div>
                    <h3 className={`font-medium ${
                      activeWorkflow === index ? 'text-white' : 'text-slate-300'
                    }`}>
                      {workflow.title}
                    </h3>
                  </div>
                </div>
              </button>
            ))}
          </div>
          
          <div className="lg:col-span-2">
            <div className="bg-slate-800 rounded-2xl p-6 md:p-8 border border-slate-700 h-full">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-teal-500 to-blue-500 flex items-center justify-center text-white">
                  {workflows[activeWorkflow].icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold">{workflows[activeWorkflow].title}</h3>
                  <p className="text-slate-300">{workflows[activeWorkflow].description}</p>
                </div>
              </div>
              
              <div className="mt-8 space-y-6">
                {workflows[activeWorkflow].steps.map((step, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
                      <span className="text-teal-400 font-medium">{index + 1}</span>
                    </div>
                    <div>
                      <p className="text-slate-200">{step}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-10 pt-6 border-t border-slate-700">
                <a 
                  href="#cta" 
                  className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 font-medium"
                >
                  Try this workflow
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Workflows;